import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { CourseContentModules, type CourseSection } from '@/components/courses/CourseContentModules';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { AuthenticatedImage } from '@/components/AuthenticatedImage';
import { ApiRequestError, api } from '@/lib/api';
import { contractSigningPath } from '@/lib/student-contract-gate';
import { PrerequisiteCourseGate } from './PrerequisiteCourseGate';

interface StudentCourseData {
  id: string;
  name: string;
  title?: string;
  description?: string | null;
  bannerImageUrl?: string | null;
  progressPercent?: number;
  sections?: CourseSection[];
}

export function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: course, isLoading, error } = useQuery({
    queryKey: ['student', 'courses', id],
    queryFn: () => api<StudentCourseData>(`/student/courses/${id}`),
    enabled: Boolean(id),
  });

  const itemCount =
    course?.sections?.reduce(
      (total, section) =>
        total +
        (section.items ?? []).filter((item) => item.itemType !== 'label' && item.isVisible !== false).length,
      0,
    ) ?? 0;

  if (!id) return null;

  return (
    <CourseHubShell
      courseId={id}
      courseName={course?.name ?? course?.title ?? 'Curso'}
      activeTab="curso"
      showProgress={itemCount > 0}
      progressPercent={course?.progressPercent ?? 0}
      studentHub
    >
      {error instanceof ApiRequestError && error.body?.code === 'CONTRACT_SIGNATURE_REQUIRED' ? (
        <section className="rounded-xl border border-dash-outline-variant bg-white p-6">
          <h2 className="text-xl font-semibold text-dash-primary">Contrato pendiente</h2>
          <p className="mt-2 text-sm text-dash-on-surface-variant">{error.message}</p>
          {error.body.contractId && (
            <Link to={contractSigningPath(error.body.contractId)} className="mt-4 inline-flex text-sm font-medium text-dash-primary">
              Firmar contrato
            </Link>
          )}
        </section>
      ) : error instanceof ApiRequestError &&
        error.body?.code === 'PREREQUISITE_COURSE_REQUIRED' &&
        error.body.prerequisiteCourse ? (
        <PrerequisiteCourseGate block={error.body.prerequisiteCourse} />
      ) : error ? (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : course ? (
        <>
          {course.bannerImageUrl && (
            <AuthenticatedImage
              src={course.bannerImageUrl}
              alt=""
              className="mb-8 h-48 w-full rounded-xl object-cover md:h-56"
            />
          )}
          <CourseContentModules
            courseId={id}
            sections={course.sections ?? []}
            adminContext={false}
            canEditBuilder={false}
          />
        </>
      ) : null}
    </CourseHubShell>
  );
}
